import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { PRIMARY_COLOR } from '../constants/colors';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock } from 'react-icons/fa';

const FooterContainer = styled.footer`
  background: #ffffff;
  color: #4a5568;
  padding: 3rem 0 1rem 0;
  margin-top: auto;
  border-top: 1px solid #f1f3f4;
  box-shadow: 0 -1px 3px rgba(0, 0, 0, 0.05);
  font-family: 'Goudy Bookletter 1911', serif;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 3rem;

  @media (min-width: 900px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const FooterSection = styled.div`
  h3 {
    color: #1a1a1a;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
    font-weight: 600;
    letter-spacing: -0.025em;
  }

  p {
    color: #6b7280;
    line-height: 1.6;
    margin-bottom: 1rem;
  }

  ul {
    list-style: none;
    padding: 0;
  }

  li {
    margin-bottom: 0.75rem;
  }

  a {
    color: #6b7280;
    text-decoration: none;
    transition: all 0.2s ease;
    font-weight: 500;

    &:hover {
      color: #5C6C4B;
      transform: translateX(2px);
    }
  }
`;

const ContactInfo = styled.div`
  p {
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #6b7280;
    font-weight: 500;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;

  a {
    background: #f8fafc;
    color: #6b7280;
    padding: 0.75rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    transition: all 0.2s ease;
    border: 1px solid #e2e8f0;

    &:hover {
      background: #5C6C4B;
      color: white;
      border-color: #5C6C4B;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px ${PRIMARY_COLOR};
    }

    svg {
      width: 1.2rem;
      height: 1.2rem;
      fill: currentColor;
    }
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding: 1.5rem 2rem 0 2rem;
  border-top: 1px solid #f1f3f4;
  margin-top: 2rem;
  color: #9ca3af;
  font-size: 0.9rem;
  font-weight: 500;
`;

const Footer = () => {
  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <h3>Moria Zeytinyağı</h3>
          <p>
          Moria, Kuzey Ege’nin gözbebeği olan Ayvalık’ın Altınova Bölgesi’nin en bereketli topraklarından, Aktepe mevkiindeki aile zeytinliğimizde doğdu. Ailemiz tarafından ekilen ve yıllarca emek verilerek büyütülen yaklaşık 100 yıllık bir zeytinliğin hikayesi gizli moria’da.
          </p>
        </FooterSection>

        <FooterSection>
          <h3>Hızlı Linkler</h3>
          <ul>
            <li><Link to="/">Ana Sayfa</Link></li>
            <li><Link to="/products">Ürünlerimiz</Link></li>
            <li><Link to="/about">Hakkımızda</Link></li>
            <li><Link to="/contact">İletişim</Link></li>
          </ul>
        </FooterSection>

        <FooterSection>
          <h3>Kurumsal </h3>
          <ul>
            <li><Link to="/docs/cerez-politikasi">Çerez Politikası</Link></li>
            <li><Link to="/docs/gizlilik-politikasi">Gizlilik Politikası</Link></li>
            <li><Link to="/docs/kullanim-kosullari">Kullanım Koşulları</Link></li>
            <li><Link to="/docs/mesafeli-satis-sozlesmesi">Mesafeli Satış Sözleşmesi</Link></li>
            <li><Link to="/docs/teslimat-ve-iade-sartlari">Teslimat, İptal ve İade Koşulları</Link></li>
          </ul>
        </FooterSection>

        <FooterSection>
          <h3>İletişim Bilgileri</h3>
          <ContactInfo>
            <p><FaMapMarkerAlt style={{ marginRight: '0.5rem' }} /> Mimar Sinan Mah. 1393 Sokak No: 7/1 Konak/İzmir </p>
            <p><FaPhone style={{ marginRight: '0.5rem' }} /> <a href="tel:+905423963510" style={{ color: 'inherit', textDecoration: 'underline'}}>+90 542 396 35 10</a></p>
            <p><FaEnvelope style={{ marginRight: '0.5rem' }} /> <a href="mailto:info@moriazeytinyagi.com" style={{ color: 'inherit', textDecoration: 'underline'}}>info@moriazeytinyagi.com</a></p>
            <p><FaClock style={{ marginRight: '0.5rem' }} /> Pazartesi - Cumartesi: <br /> 09:00 - 18:00</p>
          </ContactInfo>
          
          <SocialLinks>
            <a href="https://www.instagram.com/moriazeytinyagi/" target="_blank" rel="noopener noreferrer" title="Instagram">
              <svg viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
          </SocialLinks>
        </FooterSection>
      </FooterContent>

      {/* iyzico badge for payment compliance */}
      <div style={{ margin: '2rem auto 0 auto', textAlign: 'center' }}>
        <div style={{ fontWeight: 500, fontSize: '0.9rem', marginBottom: '0.5rem', color: '#1a1a1a' }}>
          Ödeme güvenliğiniz için iyzico altyapısı ile çalışıyoruz.
        </div>
        <img
          src="/images/iyzico-logo-pack/footer_iyzico_ile_ode/Colored/logo_band_colored.svg"
          alt="iyzico ile Öde"
          style={{ maxWidth: '260px', width: '100%', height: 'auto', margin: '0 auto', display: 'block' }}
        />
      </div>

      <Copyright>
        <p>&copy; 2025 Moria Zeytinyağı. Tüm hakları saklıdır.</p>
      </Copyright>
    </FooterContainer>
  );
};

export default Footer; 